import React, { useCallback, useEffect, useRef, useState } from 'react'
import t from '~t'
import { Avatar, Button, Icon } from '~ui'
import { LTDHUNT_API } from '../config'
import { getChatTimeline, getLtdhuntSession, sendChatMessage, getSimilarDeals, createReminder } from '../client'
import styles from './chat.module.css'

const POLL_MS = 10000
const PAGE = 50

//quick reminder presets — the actual scheduling happens server-side
//(Novu reminder-triggered with delay), so these only need offsets
const REMIND_PRESETS = [
    { key: 'remindTomorrow', ms: 24 * 3600e3 },
    { key: 'remindIn3Days', ms: 3 * 24 * 3600e3 },
    { key: 'remindInAWeek', ms: 7 * 24 * 3600e3 },
]

function timeText(ts) {
    const diff = Date.now() - ts
    const m = Math.floor(diff / 60000)
    if (m < 1) return t.s('justNow')
    if (m < 60) return `${m}m`
    const h = Math.floor(m / 60)
    if (h < 24) return `${h}h`
    return new Date(ts).toLocaleDateString()
}

function mergeMessages(list, incoming) {
    const seen = new Set(list.map(m => m.id || m.commentId))
    const merged = list.concat(incoming.filter(m => !seen.has(m.id || m.commentId)))
    return merged.sort((a, b) => a.timestamp - b.timestamp)
}

function Message({ msg }) {
    return (
        <div className={styles.msg}>
            <Avatar className={styles.avatar} src={msg.author?.avatar} />
            <div className={styles.msgBody}>
                <div className={styles.msgHead}>
                    <span className={styles.author}>{msg.author?.name}</span>
                    <span className={styles.time}>{timeText(msg.timestamp)}</span>
                </div>
                <div className={styles.content}>{msg.content}</div>
                {msg.reactions?.length > 0 && (
                    <div className={styles.reactions}>
                        {msg.reactions.map((r, i) => (
                            <span key={i} className={styles.reaction}>{r.emoji} {r.count}</span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

function SimilarDeals({ toolId }) {
    const [deals, setDeals] = useState(null)

    useEffect(() => {
        if (!toolId) return
        let alive = true
        getSimilarDeals(toolId)
            .then(res => { if (alive) setDeals(res?.data || []) })
            .catch(() => { if (alive) setDeals([]) })
        return () => { alive = false }
    }, [toolId])

    if (!deals?.length) return null
    return (
        <div className={styles.similar}>
            <div className={styles.similarTitle}>{t.s('similarDeals')}</div>
            {deals.map(d => (
                <a key={d.id} className={styles.similarItem}
                    href={`${LTDHUNT_API}/tools/${encodeURIComponent(d.tool_slug)}/deals/${encodeURIComponent(d.slug)}`}
                    target='_blank' rel='noreferrer'>
                    <span className={styles.similarName}>{d.title}</span>
                    {d.discount_percentage > 0 && (
                        <span className={styles.similarDiscount}>-{d.discount_percentage}%</span>
                    )}
                </a>
            ))}
        </div>
    )
}

//target = {toolSlug, toolName, toolLogo, toolId?, guessed?, alternatives?}
//from resolveChatTarget(); onRetarget swaps the target (fuzzy alternatives)
//fill = stretch to the container height (side panel); default caps for the popup
export default function DealChat({ target, onBack, onRetarget, fill }) {
    const [messages, setMessages] = useState(null)
    const [hasMore, setHasMore] = useState(false)
    const [failed, setFailed] = useState(false)
    //null = probing, false = probe failed (show composer anyway — a 401
    //on send still routes to sign-in), true/false split via 'out'
    const [signedIn, setSignedIn] = useState(null)
    const [draft, setDraft] = useState('')
    const [sending, setSending] = useState(false)
    const [sendError, setSendError] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [remindOpen, setRemindOpen] = useState(false)
    const [reminding, setReminding] = useState(false)
    const [reminded, setReminded] = useState(null) //Date when set, or 'err'
    const listRef = useRef(null)
    const seqRef = useRef(0) //sequence token — a stale poll can't overwrite a newer snapshot
    const countRef = useRef(0) //loaded count kept in a ref so `load` is stable (no effect re-runs)
    //target guard for async writes — slug+dealId because deal↔tool
    //switches with the same slug must still invalidate stale results
    const slugRef = useRef(null)
    slugRef.current = `${target.toolSlug}|${target.dealId || ''}`
    //slug-only guard for timeline writes: a send() that finishes after a
    //retarget calls its captured load for the OLD tool — without this
    //check the old thread would paint under the new pick
    const toolSlugRef = useRef(null)
    toolSlugRef.current = target.toolSlug

    const load = useCallback(async () => {
        const slug = target.toolSlug
        //A send from a previous target can retain this callback and invoke
        //it after retargeting — don't let that request start or apply a
        //load for the old tool. Slug-only: the thread is tool-scoped, so a
        //deal↔tool switch with the same slug stays valid.
        if (toolSlugRef.current !== slug) return
        const seq = ++seqRef.current
        try {
            //limit grows with what's on screen so a poll refresh doesn't
            //discard history loaded via "load more"
            const res = await getChatTimeline(slug, { limit: Math.max(PAGE, countRef.current) })
            if (seq !== seqRef.current || toolSlugRef.current !== slug) return
            const list = (res?.messages || []).slice().sort((a, b) => a.timestamp - b.timestamp)
            countRef.current = list.length
            setMessages(list)
            setHasMore(!!res?.hasMore)
            setFailed(false)
        } catch (e) {
            if (seq === seqRef.current && toolSlugRef.current === slug) setFailed(true)
        }
    }, [target.toolSlug])

    useEffect(() => {
        setMessages(null)
        setHasMore(false)
        countRef.current = 0
        load()
        //probe is advisory only: false = request failed, not signed out —
        //keep the composer and let a 401 on send decide
        getLtdhuntSession().then(s => {
            if (s === null) setSignedIn('out')
            else setSignedIn(true)
        })
        const timer = setInterval(load, POLL_MS)
        return () => { seqRef.current++; clearInterval(timer) }
    }, [load])

    //reminder UI is target-scoped: the side panel swaps targets without
    //remounting, and a deal↔tool switch with the same slug doesn't rerun
    //`load` — reset here or the other entity's notice/menu lingers
    useEffect(() => {
        setReminded(null)
        setRemindOpen(false)
    }, [target])

    //scroll to newest on first load and after own sends
    useEffect(() => {
        const el = listRef.current
        if (el) el.scrollTop = el.scrollHeight
    }, [messages?.length])

    const loadMore = async () => {
        if (loadingMore) return
        setLoadingMore(true)
        //no seqRef here: merging an older page is safe at any order —
        //a poll starting mid-request must not discard the user's click
        const slug = target.toolSlug
        const tag = `${slug}|${target.dealId || ''}`
        try {
            //offset pages older history (same convention as the site)
            const res = await getChatTimeline(slug, { limit: PAGE, offset: countRef.current })
            if (slugRef.current !== tag) return
            setMessages(prev => {
                const merged = mergeMessages(prev || [], res?.messages || [])
                countRef.current = merged.length
                return merged
            })
            setHasMore(!!res?.hasMore)
        } catch (e) { /* keep current list; user can retry */ } finally {
            setLoadingMore(false)
        }
    }

    const send = async () => {
        const content = draft.trim()
        if (!content || sending) return
        setSending(true)
        setSendError(false)
        try {
            await sendChatMessage(target.toolSlug, content)
            setDraft('')
            await load()
        } catch (e) {
            if (/401/.test(String(e?.message))) setSignedIn('out')
            else setSendError(true) //draft preserved for retry
        } finally {
            setSending(false)
        }
    }

    const remind = async (ms) => {
        if (reminding) return
        setReminding(true)
        //the side panel may swap the target mid-request — only apply the
        //result if we're still looking at the entity it was scheduled for
        const tag = `${target.toolSlug}|${target.dealId || ''}`
        try {
            //deal rows link to /deals/{slug}; tool-only targets fall back
            //to a 'custom' reminder pointing at the tool page
            const isDeal = target.type === 'deal' && target.dealId
            await createReminder({
                entityType: isDeal ? 'deal' : 'custom',
                entityId: isDeal ? target.dealId : null,
                title: target.toolName || target.toolSlug,
                remindAt: new Date(Date.now() + ms).toISOString(),
                originalPayload: isDeal
                    ? { deal_slug: target.dealSlug }
                    : { url: `/tools/${encodeURIComponent(target.toolSlug)}` },
            })
            if (slugRef.current !== tag) return
            setReminded(new Date())
            setRemindOpen(false)
        } catch (e) {
            if (slugRef.current !== tag) return
            if (/401/.test(String(e?.message))) setSignedIn('out')
            else setReminded('err')
        } finally {
            setReminding(false)
        }
    }

    const toolPage = `${LTDHUNT_API}/tools/${encodeURIComponent(target.toolSlug)}`

    return (
        <div className={styles.panel} data-fill={fill ? '' : undefined}>
            <div className={styles.head}>
                {onBack && (
                    <button type='button' className={styles.back} onClick={onBack} title={t.s('back')}>
                        <Icon name='back' />
                    </button>
                )}
                {target.toolLogo
                    ? <img className={styles.logo} src={target.toolLogo} alt='' />
                    : <Icon name='micro_comment' />}
                <div className={styles.headMeta}>
                    <div className={styles.toolName}>
                        {target.toolName || target.toolSlug}
                        {target.guessed && <span className={styles.guessed}>{t.s('guessedTool')}</span>}
                    </div>
                    <a className={styles.toolLink} href={toolPage} target='_blank' rel='noreferrer'>
                        {t.s('openOnLtdhunt')}
                    </a>
                </div>
                <button type='button' className={styles.remindBtn}
                    title={t.s('remindMe')}
                    data-on={remindOpen || reminded instanceof Date || undefined}
                    onClick={() => { setReminded(null); setRemindOpen(!remindOpen) }}>
                    <Icon name='reminder_add' />
                </button>
            </div>

            {target.guessed && target.alternatives?.length > 0 && onRetarget && (
                //fuzzy guesses are cheap precisely because retargeting is
                //one click — the current pick rejoins the alternatives so
                //the user can bounce back
                <div className={styles.alts}>
                    <span className={styles.altsLabel}>{t.s('alsoMatched')}</span>
                    {target.alternatives.map(alt => (
                        <button key={alt.toolSlug} type='button' className={styles.altBtn}
                            onClick={() => {
                                const { alternatives, ...rest } = target
                                onRetarget({
                                    ...alt,
                                    alternatives: [rest, ...target.alternatives.filter(a => a.toolSlug !== alt.toolSlug)],
                                })
                            }}>
                            {alt.toolName || alt.toolSlug}
                        </button>
                    ))}
                </div>
            )}

            {remindOpen && (
                <div className={styles.remindRow}>
                    {REMIND_PRESETS.map(p => (
                        <button key={p.key} type='button' className={styles.remindPreset}
                            disabled={reminding}
                            onClick={() => remind(p.ms)}>
                            {t.s(p.key)}
                        </button>
                    ))}
                </div>
            )}
            {reminded === 'err' && (
                <div className={styles.error} role='alert'>{t.s('reminderFailed')}</div>
            )}
            {reminded instanceof Date && (
                <div className={styles.remindOk} role='status'>{t.s('reminderSet')}</div>
            )}

            {target.toolId && <SimilarDeals toolId={target.toolId} />}

            <div className={styles.list} ref={listRef}>
                {hasMore && (
                    <button type='button' className={styles.more} disabled={loadingMore} onClick={loadMore}>
                        {loadingMore ? '…' : t.s('loadMore')}
                    </button>
                )}
                {messages === null && !failed && (
                    <div className={styles.empty}>…</div>
                )}
                {failed && (
                    <div className={styles.empty}>{t.s('discussionLoadFailed')}</div>
                )}
                {messages?.length === 0 && !failed && (
                    <div className={styles.empty}>
                        <Icon name='micro_comment' />
                        <div>{t.s('noDiscussionYet')}</div>
                    </div>
                )}
                {messages?.map(msg => <Message key={msg.id || msg.commentId} msg={msg} />)}
            </div>

            {sendError && (
                <div className={styles.error}>{t.s('commentSendFailed')}</div>
            )}

            {signedIn === 'out' && (
                <a className={styles.linkBtn} data-variant='primary'
                    href={`${LTDHUNT_API}/signin`} target='_blank' rel='noreferrer'>
                    {t.s('signInToDiscuss')}
                </a>
            )}
            {signedIn !== 'out' && (
                <div className={styles.composer}>
                    <textarea
                        className={styles.input}
                        value={draft}
                        placeholder={t.s('typeYourMessage')}
                        rows={2}
                        onChange={e => setDraft(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault()
                                send()
                            }
                        }} />
                    <Button variant='primary' disabled={sending || !draft.trim()} onClick={send}>
                        {sending ? '…' : t.s('send')}
                    </Button>
                </div>
            )}
        </div>
    )
}
